import React, { useState, useEffect, useRef } from "react";
// Trigger HMR
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ThemeProvider } from "./components/dashboard/ThemeProvider";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProfileProvider } from "./contexts/ProfileContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ConfirmationProvider } from "./contexts/ConfirmationContext";
import { ErrorBoundary } from "./components/landing-page/ErrorBoundary";
import { Toaster, toast } from "sonner";
import { useNavigation } from "./hooks/useNavigation";
import { AuthModal } from "./components/auth/AuthModal";
import { AnnouncementPopup } from "./components/layout/AnnouncementPopup";
import { EmailVerificationHandler } from "./components/auth/EmailVerificationHandler";
import { SupabaseRedirectHandler } from "./components/auth/SupabaseRedirectHandler";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import { ProtectedRoute } from "./components/dashboard/ProtectedRoute";
import { PublicRoute } from "./components/dashboard/PublicRoute";
import { Sidebar } from "./components/dashboard/Sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { User, Eye, ChevronRight, TrendingUp, Activity, ArrowRightLeft, BookOpen, Star, BarChart3, CandlestickChart, CreditCard, Shield, Play, LogOut, Home } from "lucide-react";
import { supabase } from "./lib/supabase";

// Import all dashboard components
import MarketRotationRRG from "./components/market-rotation/MarketRotationRRG";
import MarketRotationRRC from "./components/market-rotation/MarketRotationRRC";
import { MarketRotationSeasonality } from "./components/market-rotation/MarketRotationSeasonality";
import { MarketRotationTrendFilter } from "./components/market-rotation/MarketRotationTrendFilter";
import { Insider } from "./components/market-analysis/Insider";
import { FreeFloat } from "./components/market-analysis/FreeFloat";
import { HCL } from "./components/market-analysis/HCL";
import { BrokerTransaction } from "./components/broker-activity/BrokerTransaction";
import { NewBrokerTransaction } from "./components/broker-activity/NewBrokerTransaction";
import { BrokerSummaryPage } from "./components/broker-activity/BrokerSummaryPage";
import { NewBrokerSummary } from "./components/broker-activity/NewBrokerSummary";
import { BrokerInventoryPage } from "./components/broker-activity/BrokerInventoryPage";
import { StockTransactionDoneSummary } from "./components/stock-transaction/StockTransactionDoneSummary";
import { StockTransactionDoneDetail } from "./components/stock-transaction/StockTransactionDoneDetail";
import { OrderbookReplay } from "./components/stock-transaction/OrderbookReplay";
import { StoryAccumulationDistribution } from "./components/story/StoryAccumulationDistribution";
import { StoryAiMonitoring } from "./components/story/StoryAiMonitoring";
import { StoryAiChatbot } from "./components/story/StoryAiChatbot";
import { StoryVolume } from "./components/story/StoryVolume";
import { StorySid } from "./components/story/StorySid";
import { StoryMarketParticipant } from "./components/story/StoryMarketParticipant";
import { StoryOwnership } from "./components/story/StoryOwnership";
import { StoryForeignFlow } from "./components/story/StoryForeignFlow";
import { StoryEmitenProfile } from "./components/story/StoryEmitenProfile";
import { AstrologyLunarCalendar } from "./components/astrology/AstrologyLunarCalendar";
import BaZiCycleAnalyzer from "./components/astrology/BaZiCycleAnalysis";
import { TechnicalAnalysisTradingView } from "./components/technical-analysis/TechnicalAnalysisTradingView";
import { VideoTutorial } from "./components/video-tutorial/VideoTutorial";
import { ProfilePage } from "./components/profile/ProfilePage";
import { TradingGroupLandingPage } from "./components/trading/TradingGroupLandingPage";
import { Homepage } from "./components/home/Homepage";
import { SubscriptionPage } from "./components/subscription/SubscriptionPage";
import { PaymentHistoryPage } from "./components/subscription/PaymentHistoryPage";
import { NotSubscribed } from "./components/subscription/NotSubscribed";
import { NotBootcamp } from "./components/subscription/NotBootcamp";
import { AdminDashboard } from "./components/privilege/admin";
import { DeveloperDashboard } from "./components/privilege/developer";
import { Dashboard } from "./components/dashboard/Dashboard";
import { LandingPage } from "./components/landing-page/LandingPage";
import { PricingPage } from "./components/dashboard/PricingPage";
import { BootcampPage } from "./components/bootcamp/BootcampPage";
import { SubscriptionSuccess } from "./components/subscription/SubscriptionSuccess";
import { SubscriptionError } from "./components/subscription/SubscriptionError";
import { SubscriptionPending } from "./components/subscription/SubscriptionPending";
import { FeaturesPage } from "./components/landing-page/FeaturesPage";
import { ContactPage } from "./components/landing-page/ContactPage";
import { TermsPage } from "./components/landing-page/TermsPage";
import { PrivacyPage } from "./components/landing-page/PrivacyPage";
import { hasPremiumAccess, requiresPremiumAccess, getFeatureNameFromRoute, hasBootcampAccess } from "./utils/subscriptionAccess";
import { fetchStockMetadata } from "./utils/stockMetadataBlob";
import { fetchBrokerMetadata } from "./utils/brokerMetadataBlob";

// Dashboard Layout Component
function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { profile, isLoading, isAuthenticated, hasConnectionError } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  // IMPORTANT:
  // We REMOVE all automatic redirect logic from DashboardLayout to prevent
  // navigation throttling / infinite loops. Route protection is now handled
  // exclusively by:
  // - <ProtectedRoute> wrappers
  // - 401 handlers that force logout and hard-reload to /auth when needed.
  useEffect(() => {
    // Keep only password reset guard (no redirects here)
    const passwordResetSession = localStorage.getItem('passwordResetSession');
    const currentPath = window.location.pathname;
    const isResetPasswordPage =
      currentPath.includes('/auth/reset-password') ||
      currentPath.includes('/auth/callback');

    if (passwordResetSession === 'true' || isResetPasswordPage) {
      return;
    }

    // No other logic here – do NOT navigate from this effect.
    // This avoids any chance of redirect loops; all redirects are centralized
    // in ProtectedRoute and explicit 401 handlers.
    // We still keep hasRedirected ref for potential future use, but we don't
    // mutate it here to avoid side effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, profile, isAuthenticated, hasConnectionError]);

  // Pre-load stock and broker metadata in background once user profile is loaded (meaning they are logged in)
  useEffect(() => {
    if (profile) {
      console.log('[App] Pre-loading stockmetadata.parquet and brokermetadata.parquet...');
      fetchStockMetadata().catch(err => console.warn('[App] Failed to pre-load stock metadata:', err));
      fetchBrokerMetadata().catch(err => console.warn('[App] Failed to pre-load broker metadata:', err));
    }
  }, [profile]);

  // Show loading while profile is being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Simplified loading and redirect UI
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Mengalihkan ke login...</p>
        </div>
      </div>
    );
  }

  // If authenticated but no profile yet
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Memuat profil...</p>
        </div>
      </div>
    );
  }

  const toggleSidebar = () => setSidebarOpen((v) => !v);

  // Get current route from URL
  const getCurrentRoute = () => {
    try {
      const path = (location?.pathname || '').trim();

      // Handle exact matches first
      if (path === '/profile' || path.startsWith('/profile/')) return 'profile';
      if (path === '/subscription' || path.startsWith('/subscription/')) {
        return path.replace(/^\/+/, '');
      }
      if (path === '/') return 'home';
      if (path === '/dashboard' || path === '/dashboard/') return 'dashboard';

      // Remove leading slash and return the route
      const route = path.replace(/^\/+/, '') || 'dashboard';
      return route;
    } catch (error) {
      console.warn('Error getting current route:', error);
      return 'dashboard';
    }
  };


  // Breadcrumb mapping based on sidebar menu structure
  const breadcrumbMap: Record<string, { title: string; icon?: React.ComponentType<any>; children?: Record<string, string> }> = {
    'dashboard': { title: 'Watchlist', icon: Eye },
    'home': { title: 'Home', icon: Home },
    'market-rotation': {
      title: 'Market Rotation',
      icon: TrendingUp,
      children: {
        'rrg': 'Relative Rotation Graph',
        'rrc': 'Relative Rotation Curve',
        'seasonality': 'Seasonality',
        'trend-filter': 'Trend Filter',
      },
    },
    'broker-activity': {
      title: 'Broker Analysis',
      icon: Activity,
      children: {
        'transaction': 'Broker Transaction',
        'summary': 'Broker Summary',
        'new-summary': 'Broker Summary 5D',
        'new-summary-dev': 'Broker Summary 5D (Dev)',
        'new-transaction': 'Broker Transaction',
        'inventory': 'Broker Inventory',
      },
    },
    'stock-transaction': {
      title: 'Stock Analysis',
      icon: ArrowRightLeft,
      children: {
        'done-summary': 'Trade Footprints',
        'done-detail': 'Trade Detail',
        'orderbook-replay': 'Bid-Offer Replay',
      },
    },
    'story': {
      title: 'Screener 360°',
      icon: BookOpen,
      children: {
        'accumulation-distribution': 'Accumulation Distribution',
        'ai-monitoring': 'AI Monitoring',
        'ai-chatbot': 'AI Chatbot',
        'volume': 'Volume',
        'sid': 'SID',
        'market-participant': 'Market Participant',
        'ownership': 'Ownership',
        'emiten-profile': 'Emiten Profile',
        'foreign-flow': 'AI Prediction',
      },
    },
    'market-analysis': {
      title: 'Market Analysis',
      icon: BookOpen,
      children: {
        'insider': 'Insider',
        'free-float': 'Free Float',
        'hcl': 'High Concentration List',
      },
    },
    'astrology': { title: 'Astrology', icon: Star, children: { 'lunar': 'Ba Zi & Shio', 'bazi-cycle': 'Ba Zi Cycle Analysis' } },
    'technical-analysis': { title: 'Technical Analysis', icon: CandlestickChart },
    'video-tutorial': { title: 'Video Tutorial', icon: Play },
    'profile': { title: 'Profile', icon: User },
    'trading-group': { title: 'Trading Group', icon: TrendingUp },
    'subscription': { 
      title: 'Subscription', 
      icon: CreditCard,
      children: {
        'history': 'Payment History'
      }
    },
    'admin': { title: 'Admin', icon: Shield },
    'developer': { title: 'Developer', icon: Shield },
  };

  const getBreadcrumbParts = () => {
    try {
      const path = (location?.pathname || '').replace(/^\/+/, '');
      if (!path || path === 'dashboard') return [{ title: 'Watchlist', icon: Eye }];
      const segments = path.split('/');
      const base = segments[0];
      if (!base) return [{ title: 'Watchlist', icon: Eye }];
      const entry = breadcrumbMap[base];
      if (!entry) return [{ title: 'Watchlist', icon: Eye }];
      const parts: { title: string; icon?: React.ComponentType<any> }[] = [{ title: entry.title, ...(entry.icon && { icon: entry.icon }) }];
      if (segments[1] && entry.children) {
        const child = entry.children[segments[1]];
        if (child) parts.push({ title: child });
      }
      return parts;
    } catch (error) {
      console.warn('Error getting breadcrumb parts:', error);
      return [{ title: 'Watchlist', icon: Eye }];
    }
  };

  // Handle profile click with debounce
  const handleProfileClick = () => {
    try {
      const currentPath = location?.pathname || '';
      if (currentPath !== '/profile') {
        navigate('/profile');
      }
    } catch (error) {
      console.error('Error handling profile click:', error);
    }
  };

  const handleLogout = async () => {
    try {
      signOut();
      toast.success('Logout Berhasil!', {
        description: 'Anda telah berhasil logout.'
      });
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout Gagal', {
        description: 'Terjadi kesalahan saat logout.'
      });
    }
  };

  // Render main content based on route
  const renderMainContent = (sidebarOpen: boolean) => {
    // Get current route again inside render to ensure it's fresh
    const route = getCurrentRoute();

    // Check if user has premium access for current route
    const hasAccess = hasPremiumAccess(profile);
    const needsPremium = requiresPremiumAccess(route);
    const shouldShowNotSubscribed = needsPremium && !hasAccess;

    // Debug logging (only in development mode)
    // Uncomment below to enable debug logging:
    // console.log('Access check:', {
    //   route,
    //   subscriptionPlan: profile?.subscriptionPlan,
    //   subscriptionStatus: profile?.subscriptionStatus,
    //   hasAccess,
    //   needsPremium,
    //   shouldShowNotSubscribed
    // });

    // Show NotSubscribed if user doesn't have premium access and route requires it
    if (shouldShowNotSubscribed) {
      return <NotSubscribed featureName={getFeatureNameFromRoute(route)} />;
    }

    switch (route) {
      // Market Rotation routes
      case "market-rotation":
      case "market-rotation/rrg":
        return <MarketRotationRRG />;
      case "market-rotation/rrc":
        return <MarketRotationRRC />;
      case "market-rotation/seasonality":
        return <MarketRotationSeasonality />;
      case "market-rotation/trend-filter":
        return <MarketRotationTrendFilter />;

      // Market Analysis routes
      case "market-analysis/insider":
        return <Insider />;
      case "market-analysis/free-float":
        return <FreeFloat />;
      case "market-analysis/hcl":
        return <HCL />;

      // Broker Activity routes
      case "broker-activity":
      case "broker-activity/transaction":
        return <BrokerTransaction />;
      case "broker-activity/summary":
        return <BrokerSummaryPage />;
      case "broker-activity/new-summary":
        return <NewBrokerSummary />;
      // Salinan untuk admin/developer: kolom AI dikosongkan untuk disusun ulang (lainnya ikut Broker Summary 5D)
      case "broker-activity/new-summary-dev":
        return profile?.role === 'admin' || profile?.role === 'developer' ? <NewBrokerSummary devAi /> : <NewBrokerSummary />;
      case "broker-activity/new-transaction":
        return <NewBrokerTransaction />;
      case "broker-activity/inventory":
        return <BrokerInventoryPage />;

      // Stock Transaction routes
      case "stock-transaction":
      case "stock-transaction/done-summary":
        return <StockTransactionDoneSummary />;
      case "stock-transaction/done-detail":
        return <StockTransactionDoneDetail sidebarOpen={sidebarOpen} />;
      case "stock-transaction/orderbook-replay":
        return <OrderbookReplay />;

      // Story routes
      case "story":
      case "story/accumulation-distribution":
        return <StoryAccumulationDistribution />;
      case "story/ai-monitoring":
        return <StoryAiMonitoring />;
      case "story/ai-chatbot":
        return <StoryAiChatbot />;
      case "story/volume":
        return hasBootcampAccess(profile) ? <StoryVolume /> : <NotBootcamp featureName="Volume" />;
      case "story/sid":
        return hasBootcampAccess(profile) ? <StorySid /> : <NotBootcamp featureName="SID" />;
      case "story/market-participant":
        return <StoryMarketParticipant />;
      case "story/ownership":
        return <StoryOwnership />;
      case "story/foreign-flow":
        return <StoryForeignFlow />;
      case "story/emiten-profile":
        return <StoryEmitenProfile />;

      // Astrology routes
      case "astrology":
      case "astrology/lunar":
        return <AstrologyLunarCalendar />;
      case "astrology/bazi-cycle":
        return <BaZiCycleAnalyzer />;

      // Technical Analysis
      case "technical-analysis":
        return (
          <div className="h-full">
            <TechnicalAnalysisTradingView />
          </div>
        );
        
      // Video Tutorial
      case "video-tutorial":
        return <VideoTutorial />;

      // Homepage
      case "home":
        return <Homepage />;

      // Profile
      case "profile":
        return <ProfilePage />;

      // Trading Group Landing
      case "trading-group":
        return <TradingGroupLandingPage />;

      // Subscription
      case "subscription":
        return <SubscriptionPage />;
      case "subscription/history":
        return <PaymentHistoryPage />;

      // Admin
      case "admin":
        return <AdminDashboard />;

      // Developer
      case "developer":
        return <DeveloperDashboard />;

      // Dashboard (default)
      case "dashboard":
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        currentRoute={getCurrentRoute()}
      />

      {/* Main Content */}
      <div className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-16"}`}>
        {/* Header */}
        <header className="flex items-center justify-between px-2 sm:px-4 py-3 border-b border-border bg-card h-14 shrink-0">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0">
            {/* Mobile Hamburger Menu */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-1.5 rounded-md hover:bg-accent hover:text-accent-foreground active:bg-accent/80 transition-all duration-200 shrink-0"
              aria-label="Toggle sidebar"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Breadcrumb Navigation */}
            <nav className="flex items-center space-x-1 sm:space-x-2 text-[12px] sm:text-sm min-w-0 overflow-hidden">
              {getBreadcrumbParts().map((part, idx, arr) => (
                <div key={idx} className="flex items-center space-x-1 sm:space-x-2 min-w-0 shrink-0">
                  {idx === 0 && part.icon ? (
                    <part.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground shrink-0" />
                  ) : idx === 0 ? (
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground shrink-0" />
                  ) : null}
                  <span className={`whitespace-nowrap truncate ${idx === arr.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground hidden sm:inline-block'}`}>{part.title}</span>
                  {idx === arr.length - 1 && part.title === 'Bid-Offer Replay' && (
                    <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-400 fill-yellow-400 shrink-0" />
                  )}
                  {idx === arr.length - 1 && part.title === 'AI Prediction' && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-black bg-[#00ff00] rounded-sm leading-none flex-shrink-0 uppercase">
                      BETA
                    </span>
                  )}
                  {idx < arr.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0 hidden sm:block" />}
                </div>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3 shrink-0 ml-2">
            {/* Profile Button */}
            <div className="relative">
              <button
                onClick={handleProfileClick}
                className="flex items-center gap-2 hover:bg-accent hover:text-accent-foreground active:bg-accent/80 rounded-lg px-2 sm:px-3 py-1.5 transition-all duration-200 group relative"
                aria-label="Open profile"
                title="View Profile"
              >
                <Avatar className="h-6 w-6 sm:h-7 sm:w-7 ring-2 ring-transparent group-hover:ring-blue-200 dark:group-hover:ring-blue-800 transition-all duration-200 shrink-0">
                  <AvatarImage src={profile?.avatarUrl || profile?.avatar} alt="User Avatar" />
                  <AvatarFallback className="bg-muted text-muted-foreground group-hover:bg-blue-100 dark:group-hover:bg-blue-900 transition-colors duration-200">
                    <User className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium leading-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                    {profile?.full_name || profile?.name || 'User'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 group-hover:text-blue-500 dark:group-hover:text-blue-300 transition-colors duration-200">
                    {profile?.subscriptionStatus === 'trial'
                      ? 'Free Trial'
                      : (profile?.subscriptionPlan || 'Free') + ' Plan'}
                    {profile?.subscriptionStatus === 'trial' && profile?.subscriptionEndDate && (() => {
                      const now = new Date();
                      const endDate = new Date(profile.subscriptionEndDate);
                      const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                      return daysLeft >= 0 ? ` • ${daysLeft} day${daysLeft !== 1 ? 's' : ''} Left` : '';
                    })()}
                  </div>
                </div>
                {/* Hover indicator */}
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-blue-500 group-hover:w-8 transition-all duration-200"></div>
              </button>

            </div>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center justify-center p-2 rounded-lg hover:bg-red-500/10 hover:text-red-500 active:bg-red-500/20 transition-all duration-200"
              aria-label="Log out"
              title="Log Out"
            >
              <LogOut className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground hover:text-red-500 transition-colors duration-200" />
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col overflow-y-auto min-h-0 bg-[#0a0f20]">
          {renderMainContent(sidebarOpen)}
        </main>
      </div>
    </div>
  );
}

// Navigation wrapper component for public routes
function PublicRouteWrapper() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'signup'>('login');
  const trackFiredRef = useRef(false);
  const validateFiredRef = useRef<string | null>(null);

  // Handle URL changes to open modal based on mode param
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'login' || mode === 'register') {
      setIsAuthModalOpen(true);
      setAuthModalMode(mode === 'login' ? 'login' : 'signup');
    }

    const ref = searchParams.get('ref');
    if (ref) {
      if (validateFiredRef.current === ref) return;
      validateFiredRef.current = ref;
      
      const handleReferral = async () => {
        // @ts-ignore
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        
        try {
          // Check authentication and validate code concurrently to eliminate delay
          const [sessionRes, validateRes] = await Promise.all([
            supabase.auth.getSession(),
            fetch(`${API_URL}/api/referral/validate/${ref}`)
          ]);
          
          const validateData = await validateRes.json();
          const session = sessionRes.data.session;
          
          if (!validateData.ok) {
            toast.error(validateData.error || 'The referral code provided is invalid or no longer active.');
            // Remove from URL so it doesn't keep triggering
            searchParams.delete('ref');
            navigate({ search: searchParams.toString() }, { replace: true });
            return;
          }

          localStorage.setItem('referral_code', ref);
          
          if (session) {
            const applyRes = await fetch(`${API_URL}/api/referral/apply`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ code: ref })
            });
            const applyData = await applyRes.json();
            if (applyData.ok) {
              toast.success(`Referral code ${ref} applied to your account!`);
            }
            // Clean URL and redirect to subscription page
            searchParams.delete('ref');
            navigate('/subscription', { replace: true });
          } else {
            // Clean URL and open register modal simultaneously
            searchParams.delete('ref');
            navigate({ search: searchParams.toString() }, { replace: true });
            setIsAuthModalOpen(true);
            setAuthModalMode('signup');
          }
          
          // Track click event asynchronously once per component lifecycle
          if (trackFiredRef.current) return;
          trackFiredRef.current = true;
          
          let sessionId = localStorage.getItem('referral_session_id');
          if (!sessionId) {
            sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            localStorage.setItem('referral_session_id', sessionId);
          }

          const headers: HeadersInit = { 'Content-Type': 'application/json' };
          if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`;
          }

          await fetch(`${API_URL}/api/referral/track`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ code: ref, event_type: 'CLICK', session_id: sessionId })
          });
        } catch (error) {
          console.error('Failed to process referral:', error);
        }
      };
      handleReferral();
    }
  }, [searchParams]);

  // Handle Start Free Trial - check if already authenticated
  const handleStartTrial = () => {
    if (!authLoading && isAuthenticated) {
      // Already logged in, go directly to profile/subscription
      navigate('/subscription');
    } else {
      // Not logged in, open modal
      setAuthModalMode('signup');
      setIsAuthModalOpen(true);
      // Update URL without full navigation
      navigate('/?mode=register', { replace: true });
    }
  };

  // Handle Sign In - check if already authenticated
  const handleSignIn = () => {
    if (!authLoading && isAuthenticated) {
      // Already logged in, go directly to profile
      navigate('/profile');
    } else {
      // Not logged in, open modal
      setAuthModalMode('login');
      setIsAuthModalOpen(true);
      // Update URL without full navigation
      navigate('/?mode=login', { replace: true });
    }
  };

  // Handle Register - check if already authenticated
  const handleRegister = () => {
    if (!authLoading && isAuthenticated) {
      // Already logged in, go directly to profile
      navigate('/profile');
    } else {
      // Not logged in, go to register
      setAuthModalMode('signup');
      setIsAuthModalOpen(true);
      // Update URL without full navigation
      navigate('/?mode=register', { replace: true });
    }
  };

  return (
    <>
      <Routes>
        {/* Landing page - public route */}
        <Route
          path="/"
          element={
            <LandingPage
              onStartTrial={handleStartTrial}
              onSignIn={handleSignIn}
              onRegister={handleRegister}
            />
          }
        />
        <Route path="/features" element={<FeaturesPage />} />
        <Route
          path="/pricing"
          element={
            <PricingPage
              onSignIn={handleSignIn}
              onRegister={handleRegister}
            />
          }
        />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/bootcamp" element={<BootcampPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Email verification handler */}
        <Route
          path="/auth/verify"
          element={<EmailVerificationHandler />}
        />

        {/* Supabase redirect handler for direct email verification */}
        <Route
          path="/auth/callback"
          element={<SupabaseRedirectHandler />}
        />

        {/* Password reset page */}
        <Route
          path="/auth/reset-password"
          element={<ResetPasswordPage key={window.location.search} />}
        />

        {/* Public routes - redirect to dashboard if authenticated */}
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <LandingPage
                onStartTrial={handleStartTrial}
                onSignIn={handleSignIn}
                onRegister={handleRegister}
              />
            </PublicRoute>
          }
        />

        {/* Protected routes - redirect to auth if not authenticated */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Profile route - handled by dashboard layout */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Home route */}
        <Route
          path="/home"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Trading Group Landing Page */}
        <Route
          path="/trading-group"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Subscription route - handled by dashboard layout */}
        <Route
          path="/subscription/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Market Rotation routes */}
        <Route
          path="/market-rotation/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Market Analysis routes */}
        <Route
          path="/market-analysis/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Broker Activity routes */}
        <Route
          path="/broker-activity/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Stock Transaction routes */}
        <Route
          path="/stock-transaction/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Story routes */}
        <Route
          path="/story/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Astrology routes */}
        <Route
          path="/astrology/*"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Technical Analysis route */}
        <Route
          path="/technical-analysis"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Video Tutorial route */}
        <Route
          path="/video-tutorial"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Admin route */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Developer route */}
        <Route
          path="/developer"
          element={
            <ProtectedRoute children={<DashboardLayout />} />
          }
        />

        {/* Subscription callback routes */}
        <Route
          path="/subscription/success"
          element={<SubscriptionSuccess />}
        />
        <Route
          path="/subscription/error"
          element={<SubscriptionError />}
        />
        <Route
          path="/subscription/pending"
          element={<SubscriptionPending />}
        />
      </Routes>

      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => {
          setIsAuthModalOpen(false);
          // When closing modal, remove the mode param from URL
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('mode');
          const newSearch = newParams.toString();
          navigate(window.location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
        }} 
        initialMode={authModalMode}
      />
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <ConfirmationProvider>
          <AuthProvider>
            <ProfileProvider>
              <Router>
                <ErrorBoundary>
                  <PublicRouteWrapper />
                  <AnnouncementPopup />
                </ErrorBoundary>
              </Router>
            </ProfileProvider>
          </AuthProvider>
        </ConfirmationProvider>
      </ToastProvider>
      <Toaster
        position="bottom-right"
        richColors
        style={{ zIndex: 99999 }}
        toastOptions={{
          style: { zIndex: 99999 }
        }}
      />
    </ThemeProvider>
  );
}
